import { useState } from "react";
import { documentApiService } from "../services/documentApiService";
import { handleApiError } from "../utils/errorHandling";
import type { PolicyDocument } from "../types/api";

export interface UseDocumentManagementReturn {
  // Document types state
  documentTypes: Array<{ key: string; value: string }>;
  documentTypesLoading: boolean;
  documentTypesError: string | null;

  // Documents by type state
  documentsByType: PolicyDocument[];
  documentsByTypeLoading: boolean;
  documentsByTypeError: string | null;

  // Selected folder state
  selectedFolder: { key: string; value: string } | null;

  // Functions
  loadDocumentTypes: () => Promise<void>;
  loadDocumentsByType: (documentType: string) => Promise<void>;
  handleFolderClick: (documentType: { key: string; value: string }) => void;
  handleBackToFolders: () => void;
}

export function useDocumentManagement(): UseDocumentManagementReturn {
  // Document types state
  const [documentTypes, setDocumentTypes] = useState<
    Array<{ key: string; value: string }>
  >([]);
  const [documentTypesLoading, setDocumentTypesLoading] = useState(false);
  const [documentTypesError, setDocumentTypesError] = useState<string | null>(
    null
  );

  // Documents by type state
  const [documentsByType, setDocumentsByType] = useState<PolicyDocument[]>([]);
  const [documentsByTypeLoading, setDocumentsByTypeLoading] = useState(false);
  const [documentsByTypeError, setDocumentsByTypeError] = useState<
    string | null
  >(null);

  // Selected folder state
  const [selectedFolder, setSelectedFolder] = useState<{
    key: string;
    value: string;
  } | null>(null);

  // Load document types for Index tab
  const loadDocumentTypes = async () => {
    if (documentTypesLoading) return;

    try {
      setDocumentTypesLoading(true);
      setDocumentTypesError(null);

      const response = await documentApiService.getDocumentTypes();
      setDocumentTypes(response.data);
    } catch (err) {
      setDocumentTypesError(
        handleApiError(err, "Failed to load document types")
      );
    } finally {
      setDocumentTypesLoading(false);
    }
  };

  // Load documents by type when user clicks on a folder
  const loadDocumentsByType = async (documentType: string) => {
    if (documentsByTypeLoading) return;

    try {
      setDocumentsByTypeLoading(true);
      setDocumentsByTypeError(null);

      const response = await documentApiService.getDocumentsByType({
        document_type: documentType,
        page: 1,
        limit: 100000, // High limit to get all documents in one request
      });
      setDocumentsByType(response.data);
    } catch (err) {
      setDocumentsByTypeError(handleApiError(err, "Failed to load documents"));
    } finally {
      setDocumentsByTypeLoading(false);
    }
  };

  const handleFolderClick = (documentType: { key: string; value: string }) => {
    setSelectedFolder(documentType);
    loadDocumentsByType(documentType.key);
  };

  const handleBackToFolders = () => {
    setSelectedFolder(null);
  };

  return {
    documentTypes,
    documentTypesLoading,
    documentTypesError,
    documentsByType,
    documentsByTypeLoading,
    documentsByTypeError,
    selectedFolder,
    loadDocumentTypes,
    loadDocumentsByType,
    handleFolderClick,
    handleBackToFolders,
  };
}
